/**
 * Sing4YouPerformerStream
 *
 * Performer's live publishing component for Sing4You — uses agora-rtc-react hooks.
 * Must be inside <AgoraRTCProvider> (provided by AgoraWrapper in Sing4YouPerformerPage).
 */

import { useEffect, useRef, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  CallEnd,
  Hd,
  Mic,
  MicOff,
  Settings,
  Videocam,
  VideocamOff,
} from "@mui/icons-material";
import {
  LocalUser,
  useJoin,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  usePublish,
  useRemoteAudioTracks,
  useRemoteUsers,
} from "agora-rtc-react";
import { AGORA_APP_ID } from "../../../constants/config";
import { getAgoraToken } from "../../../services/api/agora.api";

const COLORS = {
  red: "#E50914", redHover: "#b20710",
  green: "#4CAF50", greenHover: "#45a049",
};

const VideoCallWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 100%;
  transition: all 0.3s ease-in-out;
`;

const MainContainer = styled(Box)`
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
`;

export default function Sing4YouPerformerStream({
  userId,
  videoId,
  onCallEnd,
  performerName = "Performer",
}: {
  userId: string;
  videoId: string;
  onCallEnd: () => void;
  performerName?: string;
}) {
  const [micOn, setMicOn] = useState(true);
  const [cameraOn, setCameraOn] = useState(true);
  const [settingsAnchor, setSettingsAnchor] = useState<null | HTMLElement>(null);
  const [videoQuality, setVideoQuality] = useState<"240p" | "360p" | "480p" | "720p">("720p");
  const [qualityAuto, setQualityAuto] = useState(true);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: "" });
  const [isLive, setIsLive] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<"connecting" | "connected" | "disconnected">("disconnected");
  const [token, setToken] = useState<string | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  /* ─── Token fetch ─────────────────────────────────────────────────── */
  useEffect(() => {
    setConnectionStatus("connecting");
    getAgoraToken(videoId, userId)
      .then((t) => { console.log("[Sing4YouPerformerStream] Token acquired"); setToken(t); })
      .catch((err) => { console.error("[Sing4YouPerformerStream] Token fetch failed:", err); setConnectionStatus("disconnected"); });
  }, [videoId, userId]);

  /* ─── Agora hooks ─────────────────────────────────────────────────── */
  const { isConnected } = useJoin(
    { appid: AGORA_APP_ID!, channel: videoId, token: token!, uid: userId },
    !!token
  );

  const { localMicrophoneTrack } = useLocalMicrophoneTrack(true);
  const { localCameraTrack }     = useLocalCameraTrack(true);
  usePublish([localMicrophoneTrack, localCameraTrack]);

  // Hear other participants (talent, other performers)
  const remoteUsers = useRemoteUsers();
  const { audioTracks } = useRemoteAudioTracks(remoteUsers);
  useEffect(() => { audioTracks.forEach((t) => t.play()); }, [audioTracks]);

  /* ─── Sync connection status ──────────────────────────────────────── */
  useEffect(() => {
    if (isConnected) {
      setConnectionStatus("connected");
      setIsLive(true);
      setSnackbar({ open: true, message: "Successfully connected to the stage!" });
    }
  }, [isConnected]);

  /* ─── Set encoder config (720p default) ──────────────────────────── */
  useEffect(() => {
    if (localCameraTrack) {
      localCameraTrack.setEncoderConfiguration({ width: 1280, height: 720, frameRate: 30, bitrateMax: 3000 }).catch(() => {});
    }
  }, [localCameraTrack]);

  /* ─── Controls ────────────────────────────────────────────────────── */
  const toggleMic = () => {
    localMicrophoneTrack?.setEnabled(!micOn).catch(() => {});
    setMicOn((p) => !p);
    setSnackbar({ open: true, message: !micOn ? "Microphone unmuted" : "Microphone muted" });
  };

  const toggleCamera = () => {
    localCameraTrack?.setEnabled(!cameraOn).catch(() => {});
    setCameraOn((p) => !p);
    setSnackbar({ open: true, message: !cameraOn ? "Camera turned on" : "Camera turned off" });
  };

  const handleQualityChange = async (quality: "240p" | "360p" | "480p" | "720p") => {
    const configs = {
      "240p": { width: 426,  height: 240, frameRate: 10, bitrateMax: 300  },
      "360p": { width: 640,  height: 360, frameRate: 20, bitrateMax: 800  },
      "480p": { width: 854,  height: 480, frameRate: 25, bitrateMax: 1200 },
      "720p": { width: 1280, height: 720, frameRate: 30, bitrateMax: 3000 },
    };
    await localCameraTrack?.setEncoderConfiguration(configs[quality]).catch(() => {});
    setVideoQuality(quality);
    setQualityAuto(false);
    setSnackbar({ open: true, message: `Video quality changed to ${quality}` });
    setSettingsAnchor(null);
  };

  const handleEndCall = () => {
    localMicrophoneTrack?.close();
    localCameraTrack?.close();
    onCallEnd();
  };

  const btnSize = isMobile ? "36px" : "48px";
  const btnBase = { height: btnSize, width: btnSize, padding: isMobile ? "6px" : "8px", backgroundColor: "#EEF2FF", color: "#000", borderRadius: "4px", zIndex: 3 };

  return (
    <>
      <MainContainer>
        <VideoCallWrapper sx={{ position: "relative", top: "auto", left: "auto", height: "auto", zIndex: "auto", backgroundColor: "transparent", width: "100%", opacity: 1 }}>
          {/* Status chips */}
          <Paper elevation={0} sx={{ position: "absolute", top: 16, left: 16, right: 16, zIndex: 1000, bgcolor: "transparent", border: "none", boxShadow: "none" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Chip size="small" label={isLive ? "LIVE" : "OFFLINE"} sx={{ bgcolor: isLive ? "rgba(76,175,80,0.2)" : "rgba(244,67,54,0.2)", border: "1px solid", borderColor: isLive ? COLORS.green : COLORS.red, color: isLive ? COLORS.green : COLORS.red, fontWeight: 700 }} />
                <Chip size="small" label={connectionStatus} sx={{ bgcolor: connectionStatus === "connected" ? "rgba(76,175,80,0.2)" : connectionStatus === "connecting" ? "rgba(255,193,7,0.2)" : "rgba(244,67,54,0.2)", border: "1px solid", borderColor: connectionStatus === "connected" ? COLORS.green : connectionStatus === "connecting" ? "#FFC107" : COLORS.red, color: connectionStatus === "connected" ? COLORS.green : connectionStatus === "connecting" ? "#FFC107" : COLORS.red, fontWeight: 500 }} />
              </Stack>
            </Stack>
          </Paper>

          {/* Local video */}
          <Box sx={{ width: "100%", height: "100%" }}>
            <LocalUser
              cameraOn={cameraOn}
              micOn={micOn}
              videoTrack={localCameraTrack}
              audioTrack={localMicrophoneTrack}
              style={{ width: "100%", height: "100%", borderRadius: 8, backgroundColor: "#000" }}
            />
          </Box>

          {/* Control buttons */}
          <Box display="flex" justifyContent="center" alignItems="center" mt={2} gap="16px"
            sx={{ p: 2, position: "absolute", bottom: "2%", left: "50%", transform: "translateX(-50%)", zIndex: 1000 }}>
            <IconButton onClick={toggleMic} sx={{ ...btnBase, backgroundColor: micOn ? "#EEF2FF" : COLORS.red, color: micOn ? "#000" : "#fff", "&:hover": { backgroundColor: micOn ? "#E8EAF6" : COLORS.redHover } }}>
              {micOn ? <Mic /> : <MicOff />}
            </IconButton>
            <IconButton onClick={toggleCamera} sx={{ ...btnBase, backgroundColor: cameraOn ? "#EEF2FF" : COLORS.red, color: cameraOn ? "#000" : "#fff", "&:hover": { backgroundColor: cameraOn ? "#E8EAF6" : COLORS.redHover } }}>
              {cameraOn ? <Videocam /> : <VideocamOff />}
            </IconButton>
            <IconButton onClick={(e) => setSettingsAnchor(e.currentTarget as HTMLElement)} sx={{ ...btnBase, "&:hover": { backgroundColor: "#E8EAF6" } }}>
              <Settings />
            </IconButton>
            <IconButton onClick={handleEndCall} sx={{ ...btnBase, backgroundColor: COLORS.red, color: "#fff", "&:hover": { backgroundColor: COLORS.redHover } }}>
              <CallEnd />
            </IconButton>

            <Menu anchorEl={settingsAnchor} open={Boolean(settingsAnchor)} onClose={() => setSettingsAnchor(null)}
              anchorOrigin={{ vertical: "top", horizontal: "right" }} transformOrigin={{ vertical: "bottom", horizontal: "right" }}>
              {(["240p", "360p", "480p", "720p"] as const).map((q) => (
                <MenuItem key={q} onClick={() => handleQualityChange(q)}>
                  <ListItemIcon><Hd fontSize="small" /></ListItemIcon>
                  <ListItemText primary={q} secondary={videoQuality === q ? "Current" : ""} />
                </MenuItem>
              ))}
              <MenuItem onClick={() => { setQualityAuto(true); setSettingsAnchor(null); setSnackbar({ open: true, message: "Auto quality enabled" }); }}>
                <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
                <ListItemText primary="Auto" secondary={qualityAuto ? "Current" : ""} />
              </MenuItem>
            </Menu>
          </Box>

          <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} message={snackbar.message} anchorOrigin={{ vertical: "top", horizontal: "center" }} />
        </VideoCallWrapper>
      </MainContainer>
    </>
  );
}
